import { SetupDataStore } from '../stores/setupDataStore';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Provider } from '@servicetitan/financing-api-client';
import { useIsProviderEnabledForBusinessUnit } from './useIsProviderEnabledForBusinessUnit';
import { useTenantSettings } from './useTenantSettings';
import { TurnsServiceDegradedStore } from '../stores/TurnsServiceDegradedStore';

/**
 * Configuration object describing all Turns integration state
 */
export interface TurnsIntegrationConfig {
    /**
     * Whether Turns is fully enabled and ready to use.
     * All conditions must be true: flag ON + BU enabled + tenant enabled + not degraded.
     *
     * When enabled:
     * - Show Turns-specific fields (industry type for GreenSky/GoodLeap, income type for all)
     * - Show Turns disclosure checkbox
     * - Automatically waterfall to Turns after being declined by primary provider
     */
    isEnabled: boolean;

    /**
     * Whether the provider-specific LaunchDarkly flag is enabled.
     * Used to determine which provider config to use (e.g., embedded vs standard flow).
     * This being true doesn't mean Turns is fully enabled (see isEnabled).
     */
    providerFlagEnabled: boolean;

    /** Whether Turns service is currently degraded/unavailable */
    isServiceDegraded: boolean;
}

/**
 * Consolidated hook for all Turns integration logic.
 *
 * This hook provides a single source of truth for determining:
 * - Whether Turns is enabled as a waterfall/second-look option
 * - Whether to show Turns-specific UI elements
 *
 * Use this instead of checking individual LaunchDarkly flags or other Turns-related state.
 */
export const useTurnsIntegration = (): TurnsIntegrationConfig => {
    const { setupData } = SetupDataStore.useStore();
    const flags = useFlags();
    const { isTurnsServiceDegraded } = TurnsServiceDegradedStore.useStore();

    // Check if Turns is enabled for the business unit
    const isEnabledForBusinessUnit = useIsProviderEnabledForBusinessUnit(Provider.Turns);

    const tenantSettings = useTenantSettings();
    // The "allowedOnlineEstimatesSecondLookProviders" setting is specifically for online estimates
    // (customer portal / homeowner flows). Technician flows don't require this setting.
    // Note, if "Enable TURNS integration" off for the tenant's config override, then this flag will be false.
    const isOnlineEstimatesFlow = setupData?.role !== 'technician';
    const isEnabledForTenant = isOnlineEstimatesFlow
        ? (tenantSettings?.allowedOnlineEstimatesSecondLookProviders?.includes('Turns') ?? false)
        : true;

    // Determine provider-specific configuration
    const provider = setupData?.provider?.toLowerCase();
    let providerFlagEnabled = false;

    if (provider === 'servicefinance' && flags.combinedSfAndTurnsApplication) {
        // ServiceFinance uses waterfall to Turns (submit to Turns after SF decline)
        providerFlagEnabled = true;
    } else if (provider === 'goodleap' && flags.goodleapTurns) {
        // GoodLeap uses waterfall to Turns (submit to Turns after GoodLeap decline)
        providerFlagEnabled = true;
    } else if (provider === 'greensky' && flags.greenskyTurns) {
        // GreenSky: The "greenskyTurns" flag is a misnomer - it controls the embedded flow
        // (with PIN challenge), not just Turns integration. The embedded flow is used
        // regardless of whether Turns is actually enabled (which requires BU + tenant settings).
        // When Turns is fully enabled, it uses waterfall (submit to Turns after GreenSky decline).
        providerFlagEnabled = true;
    } else if (provider === 'wellsfargo') {
        // Wells Fargo: No separate LD flag — the entire WF prequal feature is gated by the
        // `wellsfargoPrequals` flag. The remaining isEnabled conditions (BU, tenant, not degraded)
        // gate the actual waterfall behavior.
        providerFlagEnabled = true;
    }

    // Overall enablement: all conditions must be true
    const isEnabled =
        isEnabledForBusinessUnit &&
        isEnabledForTenant &&
        providerFlagEnabled &&
        !isTurnsServiceDegraded;

    return {
        isEnabled,
        providerFlagEnabled,
        isServiceDegraded: isTurnsServiceDegraded,
    };
};
